import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  UserPlus, 
  Eye, 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock, 
  AlertTriangle,
  Filter,
  ClipboardList,
  CheckCircle2,
  Plus,
  Pencil,
  Trash2,
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
  Check,
  Stethoscope,
  RotateCcw,
  Loader2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { formatDate, getDaysAlertLevel, STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, ALTA_MOTIVO_LABELS, ORIGEM_LABELS, TASK_TYPE_LABELS, todayLocal, displayCPF, HGR_SUBGRUPOS, formatOrigem } from '../lib/utils';

const HGR_SUBGRUPOS_MAP = HGR_SUBGRUPOS;

const Lista = () => {
  const { api, user, canViewFullCPF, can } = useAuth();
  const navigate = useNavigate();
  const [internacoesAtivas, setInternacoesAtivas] = useState([]);
  const [internacoesAltas, setInternacoesAltas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [medicoFilter, setMedicoFilter] = useState('all');
  const [medicos, setMedicos] = useState([]);
  const [activeTab, setActiveTab] = useState('ativos');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('nome_asc'); // Ordenação padrão: Nome A-Z
  const [viewMode, setViewMode] = useState('lista'); // 'lista' ou 'agrupado'
  
  // Quick task modal state
  const [quickTaskModalOpen, setQuickTaskModalOpen] = useState(false);
  const [selectedInternacao, setSelectedInternacao] = useState(null);
  const [novaTarefa, setNovaTarefa] = useState({
    titulo: '',
    tipo: 'visita',
    data_prevista: todayLocal(),
    prioridade: 'media',
    status: 'a_fazer',
    observacoes: ''
  });

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  
  // Reversão de alta state
  const [showReversaoModal, setShowReversaoModal] = useState(false);
  const [reversaoTarget, setReversaoTarget] = useState(null);
  const [reversaoJustificativa, setReversaoJustificativa] = useState('');
  const [savingReversao, setSavingReversao] = useState(false);
  
  const canView = can('ver_pacientes'); // detalhe da internação carrega dados do paciente
  const canEdit = can('editar_internacoes') && canView;
  const canDelete = can('excluir_internacoes');
  const canCreateInt = can('criar_internacoes');
  const canCreateTask = can('criar_tarefas');
  const canReverterAlta = can('reverter_alta');
  
  // Default columns for Ativos
  const defaultColumnsAtivos = ['prontuario', 'nome', 'cpf', 'data_internacao', 'dias', 'origem_grupo', 'origem_subgrupo', 'equipe', 'medico', 'telefone'];
  // Default columns for De alta
  const defaultColumnsAltas = ['prontuario', 'nome', 'cpf', 'data_internacao', 'data_alta', 'motivo_alta', 'origem_grupo', 'equipe', 'medico', 'telefone'];
  
  const [selectedColumns, setSelectedColumns] = useState(defaultColumnsAtivos);
  
  // All available columns
  const availableColumns = {
    prontuario: 'Nº Prontuário',
    nome: 'Nome',
    cpf: 'CPF',
    data_nascimento: 'Data de Nascimento',
    data_internacao: 'Data de Internação',
    dias: 'Dias de Internação (D+X)',
    origem_grupo: 'Grupo de Origem',
    origem_subgrupo: 'Subgrupo (HGR)',
    equipe: 'Equipe',
    medico: 'Médico(a) responsável',
    medico_solicitante: 'Médico(a) solicitante',
    diagnostico: 'Diagnóstico Principal',
    data_alta: 'Data da Alta',
    motivo_alta: 'Motivo da Alta',
    telefone: 'Telefone/Contato',
    endereco: 'Endereço',
    responsavel: 'Responsável'
  };
  
  // Columns available per tab
  const columnsForAtivos = ['nome', 'cpf', 'data_nascimento', 'data_internacao', 'dias', 'origem_grupo', 'origem_subgrupo', 'equipe', 'medico', 'medico_solicitante', 'diagnostico', 'telefone', 'endereco', 'responsavel'];
  const columnsForAltas = ['nome', 'cpf', 'data_nascimento', 'data_internacao', 'dias', 'data_alta', 'motivo_alta', 'origem_grupo', 'origem_subgrupo', 'equipe', 'medico', 'medico_solicitante', 'diagnostico', 'telefone', 'endereco', 'responsavel'];

  // Função para agrupar altas por motivo
  const groupAltasByMotivo = (altas) => {
    const grupos = {
      obito: { label: 'Alta por Óbito', items: [], color: 'bg-gray-100 dark:bg-gray-800' },
      alta_clinica: { label: 'Alta Clínica', items: [], color: 'bg-green-50 dark:bg-green-900/20' },
      alta_administrativa: { label: 'Alta Administrativa', items: [], color: 'bg-yellow-50 dark:bg-yellow-900/20' },
      transferencia: { label: 'Transferência', items: [], color: 'bg-blue-50 dark:bg-blue-900/20' },
      outros: { label: 'Outros', items: [], color: 'bg-gray-50 dark:bg-gray-800/50' }
    };

    altas.forEach(alta => {
      let motivo = alta.motivo_alta?.toLowerCase() || 'outros';
      // Mapear motivos antigos
      if (motivo === 'desistencia' || motivo === 'desistência') {
        motivo = 'alta_administrativa';
      }
      if (grupos[motivo]) {
        grupos[motivo].items.push(alta);
      } else {
        grupos.outros.items.push(alta);
      }
    });

    return grupos;
  };

  useEffect(() => {
    fetchInternacoes();
  }, []);

  const fetchInternacoes = async () => {
    try {
      const [ativasRes, altasRes, medicosRes] = await Promise.all([
        api.get('/internacoes?ativas=true'),
        api.get('/internacoes?ativas=false'),
        api.get('/medicos')
      ]);
      setInternacoesAtivas(ativasRes.data);
      setInternacoesAltas(altasRes.data.filter(i => i.status_kanban === 'alta_efetivada'));
      setMedicos(medicosRes.data || []);
    } catch (error) {
      console.error('Error fetching internacoes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (e, internacao) => {
    e.stopPropagation();
    navigate(`/pacientes/${internacao.paciente_id}/internacao/${internacao.id}`);
  };

  const handleReverterAltaClick = (e, internacao) => {
    e.stopPropagation();
    setReversaoTarget(internacao);
    setReversaoJustificativa('');
    setShowReversaoModal(true);
  };

  const handleConfirmReversao = async () => {
    if (!reversaoTarget) return;
    if (reversaoJustificativa.trim().length < 10) {
      toast.error('Justificativa obrigatória (mínimo 10 caracteres)');
      return;
    }
    setSavingReversao(true);
    try {
      await api.post(`/internacoes/${reversaoTarget.id}/reverter-alta`, {
        justificativa: reversaoJustificativa.trim(),
        status_retorno: 'ativo_estavel'
      });
      toast.success('Alta revertida com sucesso!');
      setShowReversaoModal(false);
      // Move from altas to ativos
      setInternacoesAltas(prev => prev.filter(i => i.id !== reversaoTarget.id));
      fetchInternacoes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reverter alta');
    } finally {
      setSavingReversao(false);
    }
  };

  const handleEditClick = (e, internacao) => {
    e.stopPropagation();
    navigate(`/pacientes/${internacao.paciente_id}/internacao/${internacao.id}?edit=true`);
  };

  const handleDeleteClick = (e, internacao) => {
    e.stopPropagation();
    setItemToDelete(internacao);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await api.delete(`/internacoes/${itemToDelete.id}`);
      toast.success('Internação excluída com sucesso!');
      fetchInternacoes();
    } catch (error) {
      toast.error('Erro ao excluir internação');
      console.error('Error deleting:', error);
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handlePatientNameClick = (e, internacao) => {
    e.stopPropagation();
    if (!canCreateTask) return;
    setSelectedInternacao(internacao);
    setNovaTarefa({
      titulo: '',
      tipo: 'visita',
      data_prevista: todayLocal(),
      prioridade: 'media',
      status: 'a_fazer',
      observacoes: ''
    });
    setQuickTaskModalOpen(true);
  };

  const handleCreateQuickTask = async () => {
    if (!selectedInternacao || !novaTarefa.titulo) return;
    
    try {
      await api.post('/tarefas', {
        ...novaTarefa,
        internacao_id: selectedInternacao.id
      });
      toast.success(`Tarefa criada para ${selectedInternacao.paciente_nome}!`);
      setQuickTaskModalOpen(false);
      setSelectedInternacao(null);
      fetchInternacoes(); // Refresh to update task counts
    } catch (error) {
      toast.error('Erro ao criar tarefa');
      console.error('Error creating task:', error);
    }
  };

  const internacoes = activeTab === 'ativos' ? internacoesAtivas : internacoesAltas;

  // Função de ordenação
  const sortInternacoes = (list) => {
    const sorted = [...list];
    switch (sortBy) {
      case 'prontuario_asc':
        return sorted.sort((a, b) => (a.numero_prontuario || '').localeCompare(b.numero_prontuario || ''));
      case 'prontuario_desc':
        return sorted.sort((a, b) => (b.numero_prontuario || '').localeCompare(a.numero_prontuario || ''));
      case 'nome_asc':
        return sorted.sort((a, b) => (a.paciente_nome || '').localeCompare(b.paciente_nome || '', 'pt-BR'));
      case 'nome_desc':
        return sorted.sort((a, b) => (b.paciente_nome || '').localeCompare(a.paciente_nome || '', 'pt-BR'));
      case 'dias_asc':
        return sorted.sort((a, b) => (a.dias_internacao || 0) - (b.dias_internacao || 0));
      case 'dias_desc':
        return sorted.sort((a, b) => (b.dias_internacao || 0) - (a.dias_internacao || 0));
      case 'data_asc':
        return sorted.sort((a, b) => new Date(a.data_internacao) - new Date(b.data_internacao));
      case 'data_desc':
        return sorted.sort((a, b) => new Date(b.data_internacao) - new Date(a.data_internacao));
      case 'alta_desc':
        return sorted.sort((a, b) => new Date(b.data_alta || 0) - new Date(a.data_alta || 0));
      case 'alta_asc':
        return sorted.sort((a, b) => new Date(a.data_alta || 0) - new Date(b.data_alta || 0));
      case 'motivo_asc':
        return sorted.sort((a, b) => (a.motivo_alta || '').localeCompare(b.motivo_alta || '', 'pt-BR'));
      case 'motivo_desc':
        return sorted.sort((a, b) => (b.motivo_alta || '').localeCompare(a.motivo_alta || '', 'pt-BR'));
      case 'diag_asc':
        return sorted.sort((a, b) => (a.diagnostico_principal || '').localeCompare(b.diagnostico_principal || '', 'pt-BR'));
      case 'diag_desc':
        return sorted.sort((a, b) => (b.diagnostico_principal || '').localeCompare(a.diagnostico_principal || '', 'pt-BR'));
      default:
        return sorted;
    }
  };

  // Cabeçalho clicável: alterna asc/desc da coluna (campo = prefixo da chave de ordenação)
  const toggleSort = (campo) => setSortBy((prev) => (prev === `${campo}_desc` ? `${campo}_asc` : `${campo}_desc`));
  const SortHead = ({ campo, children }) => {
    const ativo = sortBy.startsWith(`${campo}_`);
    const Icon = !ativo ? ArrowUpDown : sortBy.endsWith('_desc') ? ArrowDown : ArrowUp;
    return (
      <TableHead>
        <button type="button" onClick={() => toggleSort(campo)} className={`inline-flex items-center gap-1 hover:text-foreground ${ativo ? 'text-foreground font-semibold' : ''}`} data-testid={`sort-${campo}`}>
          {children}<Icon className="h-3.5 w-3.5" />
        </button>
      </TableHead>
    );
  };

  const filteredInternacoes = sortInternacoes(internacoes.filter((i) => {
    const searchLower = search.toLowerCase().trim();
    const searchDigits = search.replace(/\D/g, '');
    
    const matchesSearch = 
      (i.numero_prontuario || '').includes(searchDigits && searchDigits.length >= 1 ? searchDigits : '§') ||
      i.paciente_nome?.toLowerCase().includes(searchLower) ||
      i.diagnostico_principal?.toLowerCase().includes(searchLower) ||
      (searchDigits.length >= 3 && (i.paciente_cpf || i.cpf || '').replace(/\D/g, '').includes(searchDigits));
    
    const matchesStatus = activeTab === 'altas' || statusFilter === 'all' || i.status_kanban === statusFilter;
    
    const matchesMedico = medicoFilter === 'all' 
      || (medicoFilter === 'sem_medico' && !i.medico_id && !i.medico_responsavel_id)
      || i.medico_id === medicoFilter || i.medico_responsavel_id === medicoFilter;
    
    return matchesSearch && matchesStatus && matchesMedico;
  }));

  const handleRowClick = (internacao) => {
    if (!canView) return;
    navigate(`/pacientes/${internacao.paciente_id}/internacao/${internacao.id}`);
  };

  // Export modal open handler - set default columns based on tab
  const handleOpenExportModal = () => {
    const defaults = activeTab === 'ativos' ? defaultColumnsAtivos : defaultColumnsAltas;
    setSelectedColumns(defaults);
    setExportModalOpen(true);
  };

  // Toggle column selection
  const toggleColumn = (columnId) => {
    setSelectedColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(c => c !== columnId)
        : [...prev, columnId]
    );
  };

  // Select all columns
  const selectAllColumns = () => {
    const cols = activeTab === 'ativos' ? columnsForAtivos : columnsForAltas;
    setSelectedColumns(cols);
  };

  // Deselect all columns
  const deselectAllColumns = () => {
    setSelectedColumns([]);
  };

  // Format CPF for display
  const formatCPF = (cpf) => {
    if (!cpf) return '';
    const clean = cpf.replace(/\D/g, '');
    if (clean.length !== 11) return cpf;
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Get column value for export
  const getColumnValue = (internacao, columnId) => {
    switch (columnId) {
      case 'prontuario':
        return internacao.numero_prontuario || '';
      case 'nome':
        return internacao.paciente_nome || '';
      case 'cpf':
        return displayCPF(internacao.paciente_cpf || internacao.cpf, canViewFullCPF);
      case 'data_nascimento':
        return formatDate(internacao.paciente_data_nascimento) || '—';
      case 'data_internacao':
        return formatDate(internacao.data_internacao) || '';
      case 'dias':
        return `D+${internacao.dias_internacao || 0}`;
      case 'origem':
        return internacao.origem_display || ORIGEM_LABELS[internacao.origem] || internacao.origem || '';
      case 'origem_grupo':
        return internacao.origem_grupo || '';
      case 'origem_subgrupo':
        return internacao.origem_subgrupo ? (HGR_SUBGRUPOS_MAP[internacao.origem_subgrupo] || internacao.origem_subgrupo) : '';
      case 'equipe':
        return internacao.equipe_nome || '';
      case 'medico':
        return internacao.medico_nome || '—';
      case 'medico_solicitante':
        return internacao.medico_solicitante_nome || '—';
      case 'diagnostico':
        return internacao.diagnostico_principal || '';
      case 'data_alta':
        return formatDate(internacao.data_alta) || '';
      case 'motivo_alta':
        return ALTA_MOTIVO_LABELS[internacao.motivo_alta] || internacao.motivo_alta || '';
      case 'telefone':
        return internacao.telefone || internacao.paciente_telefone || '';
      case 'endereco':
        return internacao.endereco || internacao.paciente_endereco || '';
      case 'responsavel':
        return internacao.responsavel || '';
      default:
        return '';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const data = filteredInternacoes;
    const headers = selectedColumns.map(col => availableColumns[col]);
    const rows = data.map(item => 
      selectedColumns.map(col => {
        const value = getColumnValue(item, col);
        // Escape quotes and wrap in quotes if contains comma
        if (value.includes(',') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
    );
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const tabLabel = activeTab === 'ativos' ? 'Ativos' : 'DeAlta';
    link.href = url;
    link.download = `Internacoes_${tabLabel}_${todayLocal()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exportado ${data.length} registros para CSV`);
    setExportModalOpen(false);
  };

  // Export to Excel (using simple HTML table format that Excel can open)
  const exportToExcel = () => {
    const data = filteredInternacoes;
    const headers = selectedColumns.map(col => availableColumns[col]);
    const tabLabel = activeTab === 'ativos' ? 'Ativos' : 'De Alta';
    
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head><meta charset="UTF-8"></head>
      <body>
      <table border="1">
        <tr><th colspan="${headers.length}" style="font-size:16px;font-weight:bold;background:#f0f0f0;">
          Internações — ${tabLabel} (${new Date().toLocaleString('pt-BR', { timeZone: 'America/Manaus' })})
        </th></tr>
        <tr>${headers.map(h => `<th style="background:#e0e0e0;font-weight:bold;">${h}</th>`).join('')}</tr>
    `;
    
    data.forEach(item => {
      html += '<tr>';
      selectedColumns.forEach(col => {
        html += `<td>${getColumnValue(item, col)}</td>`;
      });
      html += '</tr>';
    });
    
    html += '</table></body></html>';
    
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Internacoes_${tabLabel.replace(' ', '')}_${todayLocal()}.xls`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exportado ${data.length} registros para Excel`);
    setExportModalOpen(false);
  };

  // Escape HTML to prevent XSS in print window
  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  // Print function
  const handlePrint = () => {
    const data = filteredInternacoes;
    const headers = selectedColumns.map(col => availableColumns[col]);
    const tabLabel = activeTab === 'ativos' ? 'Ativos' : 'De Alta';
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Popup bloqueado. Permita popups para imprimir.'); return; }
    const html = `
      <html>
      <head>
        <title>Internações — ${escapeHtml(tabLabel)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .subtitle { color: #666; font-size: 12px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          tr:nth-child(even) { background: #fafafa; }
          .footer { margin-top: 20px; font-size: 10px; color: #999; }
          @media print {
            body { margin: 0; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Internações — ${escapeHtml(tabLabel)}</h1>
        <div class="subtitle">Gerado em: ${escapeHtml(new Date().toLocaleString('pt-BR', { timeZone: 'America/Manaus' }))} | Total: ${data.length} registros</div>
        <table>
          <thead>
            <tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${data.map(item => `
              <tr>${selectedColumns.map(col => `<td>${escapeHtml(getColumnValue(item, col))}</td>`).join('')}</tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">Lifeport Home — Sistema de Gestão de Internações</div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
    setExportModalOpen(false);
  };

  // Handle export based on format
  const handleExport = () => {
    if (selectedColumns.length === 0) {
      toast.error('Selecione pelo menos uma coluna para exportar');
      return;
    }
    
    if (exportFormat === 'csv') {
      exportToCSV();
    } else if (exportFormat === 'excel') {
      exportToExcel();
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="lista-page">
        {/* Header with counters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Internações</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie todas as internações do sistema
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Counters */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <span className="text-muted-foreground">Internados:</span>
                <span className="font-bold text-green-600">{internacoesAtivas.length}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800" title="Internações (prontuários) ativas + encerradas">
                <span className="text-muted-foreground">Internações:</span>
                <span className="font-bold text-blue-600" data-testid="contador-internacoes">{internacoesAtivas.length + internacoesAltas.length}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-900/20 rounded-lg border border-slate-200 dark:border-slate-700" title="Pacientes distintos (um paciente pode ter várias internações)">
                <span className="text-muted-foreground">Pacientes únicos:</span>
                <span className="font-bold" data-testid="contador-pacientes-unicos">{new Set([...internacoesAtivas, ...internacoesAltas].map((i) => i.paciente_id)).size}</span>
              </div>
            </div>
            {can('exportar_internacoes') && (
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={handleOpenExportModal}
              data-testid="btn-exportar-lista"
            >
              <Download className="h-4 w-4" />
              Exportar / Imprimir
            </Button>
            )}
            {can('criar_pacientes') && (
            <Link to="/pacientes/novo">
              <Button className="gap-2" data-testid="btn-novo-paciente-lista">
                <UserPlus className="h-4 w-4" />
                Novo Paciente
              </Button>
            </Link>
            )}
          </div>
        </div>

        {/* Tabs - Ativos / De alta */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="ativos" data-testid="tab-ativos" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Ativos
              <Badge variant="secondary">{internacoesAtivas.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="altas" data-testid="tab-altas" className="gap-2">
              <Clock className="h-4 w-4" />
              De Alta
              <Badge variant="secondary">{internacoesAltas.length}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Pesquisar por nome, CPF, nº prontuário ou diagnóstico..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-lista"
                  />
                </div>
                {activeTab === 'ativos' && (
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="select-status-filter">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filtrar por status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'alta_efetivada').map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={medicoFilter} onValueChange={setMedicoFilter}>
                  <SelectTrigger className="w-full sm:w-52" data-testid="select-medico-filter">
                    <Stethoscope className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filtrar por médico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os médicos</SelectItem>
                    <SelectItem value="sem_medico">Sem médico</SelectItem>
                    {medicos.filter(m => m.ativo !== false).map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nome_asc">Nome (A–Z)</SelectItem>
                    <SelectItem value="nome_desc">Nome (Z–A)</SelectItem>
                    <SelectItem value="dias_desc">Dias (Maior)</SelectItem>
                    <SelectItem value="dias_asc">Dias (Menor)</SelectItem>
                    <SelectItem value="data_desc">Data Internação (Recente)</SelectItem>
                    <SelectItem value="data_asc">Data Internação (Antiga)</SelectItem>
                    {activeTab === 'altas' && (
                      <>
                        <SelectItem value="alta_desc">Data Alta (Recente)</SelectItem>
                        <SelectItem value="alta_asc">Data Alta (Antiga)</SelectItem>
                        <SelectItem value="motivo_asc">Motivo Alta (A–Z)</SelectItem>
                        <SelectItem value="motivo_desc">Motivo Alta (Z–A)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Active Tab Content */}
          <TabsContent value="ativos" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº Prontuário</TableHead>
                        <TableHead>Paciente</TableHead>
                        <TableHead>Diagnóstico</TableHead>
                        <TableHead>Data Internação</TableHead>
                        <TableHead>Dias Internado</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tarefas</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          </TableRow>
                        ))
                      ) : filteredInternacoes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Nenhuma internação encontrada
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredInternacoes.map((internacao) => {
                          const alertLevel = getDaysAlertLevel(internacao.dias_internacao);
                          const taskPriority = internacao.prioridade_maxima_tarefa;
                          
                          const getPriorityBadgeClass = () => {
                            if (!taskPriority) return '';
                            const classes = {
                              alta: 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20',
                              media: 'border-yellow-500 text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
                              baixa: 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                            };
                            return classes[taskPriority];
                          };
                          
                          return (
                            <TableRow 
                              key={internacao.id} 
                              data-testid={`lista-row-${internacao.id}`}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleRowClick(internacao)}
                            >
                              <TableCell className="font-mono text-xs" data-testid={`prontuario-${internacao.id}`}>{internacao.numero_prontuario || '—'}{internacao.readmissao && <span className="ml-1 text-[10px] text-amber-700" title="Readmissão">R</span>}</TableCell>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {taskPriority && (
                                    <div 
                                      className={`w-2.5 h-2.5 rounded-full animate-pulse ${
                                        taskPriority === 'alta' ? 'bg-red-500' :
                                        taskPriority === 'media' ? 'bg-yellow-500' :
                                        'bg-blue-500'
                                      }`}
                                      title={`Tarefa pendente: Prioridade ${taskPriority}`}
                                    />
                                  )}
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={(e) => handlePatientNameClick(e, internacao)}
                                          className="text-left hover:text-primary hover:underline underline-offset-2 transition-colors flex items-center gap-1"
                                        >
                                          {internacao.paciente_nome}
                                          <Plus className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Clique para adicionar tarefa</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </TableCell>
                              <TableCell className="max-w-xs truncate">
                                {internacao.diagnostico_principal}
                              </TableCell>
                              <TableCell>{formatDate(internacao.data_internacao)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {alertLevel !== 'normal' && (
                                    <AlertTriangle className={`h-4 w-4 ${
                                      alertLevel === 'critical' ? 'text-red-500' : 'text-yellow-500'
                                    }`} />
                                  )}
                                  <Badge 
                                    variant="outline"
                                    className={`
                                      ${alertLevel === 'critical' ? 'border-red-500 text-red-600' : ''}
                                      ${alertLevel === 'warning' ? 'border-yellow-500 text-yellow-600' : ''}
                                    `}
                                  >
                                    <Clock className="h-3 w-3 mr-1" />
                                    D+{internacao.dias_internacao}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={STATUS_COLORS[internacao.status_kanban]}>
                                  {STATUS_LABELS[internacao.status_kanban]}
                                </Badge>
                              </TableCell>
                              <TableCell>{internacao.tarefas_pendentes_count > 0 ? (
                                  <Badge 
                                    variant="outline"
                                    className={getPriorityBadgeClass()}
                                  >
                                    <ClipboardList className="h-3 w-3 mr-1" />
                                    {internacao.tarefas_pendentes_count} {PRIORITY_LABELS[taskPriority]}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  {canView && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={(e) => handleViewClick(e, internacao)}
                                    data-testid={`btn-ver-${internacao.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    Ver
                                  </Button>
                                  )}
                                  {canEdit && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={(e) => handleEditClick(e, internacao)}
                                    data-testid={`btn-editar-${internacao.id}`}
                                  >
                                    <Pencil className="h-4 w-4 mr-1" />
                                    Editar
                                  </Button>
                                  )}
                                  {canDelete && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => handleDeleteClick(e, internacao)}
                                    data-testid={`btn-excluir-${internacao.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Altas Tab Content */}
          <TabsContent value="altas" className="mt-4">
            {/* View Mode Toggle */}
            <div className="flex justify-end mb-4">
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Visualização" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lista">Lista única</SelectItem>
                  <SelectItem value="agrupado">Agrupar por motivo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {viewMode === 'lista' ? (
              /* Lista única (modo original) */
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortHead campo="prontuario">Nº Prontuário</SortHead>
                          <SortHead campo="nome">Paciente</SortHead>
                          <SortHead campo="diag">Diagnóstico</SortHead>
                          <SortHead campo="data">Data Internação</SortHead>
                          <SortHead campo="alta">Data Alta</SortHead>
                          <SortHead campo="motivo">Motivo Alta</SortHead>
                          <SortHead campo="dias">Tempo Total</SortHead>
                          <TableHead>Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          [...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                              <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            </TableRow>
                          ))
                        ) : filteredInternacoes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                              Nenhuma alta encontrada
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredInternacoes.map((internacao) => {
                            const diasTotal = internacao.dias_internacao || 1;
                            
                            return (
                              <TableRow 
                                key={internacao.id} 
                                data-testid={`alta-row-${internacao.id}`}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => handleRowClick(internacao)}
                              >
                                <TableCell className="font-mono text-xs" data-testid={`prontuario-${internacao.id}`}>{internacao.numero_prontuario || '—'}</TableCell>
                                <TableCell className="font-medium">
                                  {internacao.paciente_nome}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                  {internacao.diagnostico_principal}
                                </TableCell>
                                <TableCell>{formatDate(internacao.data_internacao)}</TableCell>
                                <TableCell>
                                  {internacao.data_alta ? (
                                    formatDate(internacao.data_alta)
                                  ) : (
                                    <span className="text-muted-foreground italic text-sm">Não informada</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {internacao.comentario_alta_administrativa ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400 cursor-help">
                                            {ALTA_MOTIVO_LABELS[internacao.motivo_alta] || internacao.motivo_alta || 'Não informado'}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-xs">
                                          <p className="font-semibold text-xs mb-1">Justificativa:</p>
                                          <p className="text-xs">{internacao.comentario_alta_administrativa}</p>
                                          {internacao.alta_administrativa_usuario && (
                                            <p className="text-xs text-muted-foreground mt-1">Por: {internacao.alta_administrativa_usuario}</p>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <Badge variant="outline">
                                      {ALTA_MOTIVO_LABELS[internacao.motivo_alta] || internacao.motivo_alta || 'Não informado'}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <Badge variant="secondary">
                                          <Clock className="h-3 w-3 mr-1" />
                                          D+{diasTotal}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{diasTotal} dia{diasTotal > 1 ? 's' : ''} de internação</p>
                                        <p className="text-xs text-muted-foreground">Primeiro dia conta como D+1</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    {canView && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={(e) => handleViewClick(e, internacao)}
                                      data-testid={`btn-ver-alta-${internacao.id}`}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      Ver
                                    </Button>
                                    )}
                                    {canReverterAlta && (
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        className="border-green-400 text-green-700 hover:bg-green-50"
                                        onClick={(e) => handleReverterAltaClick(e, internacao)}
                                        data-testid={`btn-reverter-alta-${internacao.id}`}
                                      >
                                        <RotateCcw className="h-4 w-4 mr-1" />
                                        Reverter
                                      </Button>
                                    )}
                                    {canCreateInt && (
                                    <Link 
                                      to={`/pacientes/${internacao.paciente_id}/nova-internacao`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Button variant="outline" size="sm" data-testid={`btn-nova-internacao-${internacao.id}`}>
                                        <Plus className="h-4 w-4 mr-1" />
                                        Nova Admissão
                                      </Button>
                                    </Link>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Modo agrupado por motivo */
              <div className="space-y-6">
                {Object.entries(groupAltasByMotivo(filteredInternacoes)).map(([motivo, grupo]) => (
                  grupo.items.length > 0 && (
                    <Card key={motivo} className={grupo.color}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center justify-between">
                          <span>{grupo.label}</span>
                          <Badge variant="secondary" className="text-lg px-3">
                            {grupo.items.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <SortHead campo="nome">Paciente</SortHead>
                                <SortHead campo="diag">Diagnóstico</SortHead>
                                <SortHead campo="data">Data Internação</SortHead>
                                <SortHead campo="alta">Data Alta</SortHead>
                                <SortHead campo="dias">Tempo Total</SortHead>
                                <TableHead>Ações</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {grupo.items.map((internacao) => {
                                const diasTotal = internacao.dias_internacao || 1;
                                return (
                                  <TableRow 
                                    key={internacao.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => handleRowClick(internacao)}
                                  >
                                    <TableCell className="font-medium">
                                      {internacao.paciente_nome}
                                    </TableCell>
                                    <TableCell className="max-w-xs truncate">
                                      {internacao.diagnostico_principal}
                                    </TableCell>
                                    <TableCell>{formatDate(internacao.data_internacao)}</TableCell>
                                    <TableCell>
                                      {internacao.data_alta ? (
                                        formatDate(internacao.data_alta)
                                      ) : (
                                        <span className="text-muted-foreground italic text-sm">Não informada</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="secondary">
                                        <Clock className="h-3 w-3 mr-1" />
                                        D+{diasTotal}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                        {canView && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={(e) => handleViewClick(e, internacao)}
                                        >
                                          <Eye className="h-4 w-4 mr-1" />
                                          Ver
                                        </Button>
                                        )}
                                        {canCreateInt && (
                                        <Link 
                                          to={`/pacientes/${internacao.paciente_id}/nova-internacao`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Button variant="outline" size="sm">
                                            <Plus className="h-4 w-4 mr-1" />
                                            Nova
                                          </Button>
                                        </Link>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  )
                ))}
                {filteredInternacoes.length === 0 && (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      Nenhuma alta encontrada
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Internação</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir esta internação de <strong>{itemToDelete?.paciente_nome}</strong>?
                <br /><br />
                Esta ação não pode ser desfeita. Todas as tarefas e planejamentos associados serão arquivados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Quick Task Modal */}
        <Dialog open={quickTaskModalOpen} onOpenChange={setQuickTaskModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Tarefa</DialogTitle>
              <DialogDescription>
                Adicionar tarefa para <strong>{selectedInternacao?.paciente_nome}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  placeholder="Título da tarefa"
                  value={novaTarefa.titulo}
                  onChange={(e) => setNovaTarefa({...novaTarefa, titulo: e.target.value})}
                  data-testid="input-quick-tarefa-titulo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={novaTarefa.tipo}
                    onValueChange={(v) => setNovaTarefa({...novaTarefa, tipo: v})}
                  >
                    <SelectTrigger data-testid="select-quick-tarefa-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select 
                    value={novaTarefa.prioridade}
                    onValueChange={(v) => setNovaTarefa({...novaTarefa, prioridade: v})}
                  >
                    <SelectTrigger data-testid="select-quick-tarefa-prioridade">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Prevista</Label>
                <Input
                  type="date"
                  value={novaTarefa.data_prevista}
                  onChange={(e) => setNovaTarefa({...novaTarefa, data_prevista: e.target.value})}
                  data-testid="input-quick-tarefa-data"
                />
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  placeholder="Observações..."
                  value={novaTarefa.observacoes}
                  onChange={(e) => setNovaTarefa({...novaTarefa, observacoes: e.target.value})}
                  data-testid="input-quick-tarefa-obs"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickTaskModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateQuickTask} disabled={!novaTarefa.titulo} data-testid="btn-salvar-quick-tarefa">
                <Plus className="h-4 w-4 mr-2" />
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Exportar lista de internações
              </DialogTitle>
              <DialogDescription>
                Exportando: <strong>{activeTab === 'ativos' ? 'Internados (Ativos)' : 'De Alta'}</strong>
                {' — '}{filteredInternacoes.length} registros
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Column Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Selecionar campos para exportação</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllColumns} className="text-xs h-7">
                      Marcar todos
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAllColumns} className="text-xs h-7">
                      Desmarcar todos
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 p-3 bg-muted/50 rounded-lg max-h-60 overflow-y-auto">
                  {(activeTab === 'ativos' ? columnsForAtivos : columnsForAltas).map(colId => (
                    <div 
                      key={colId}
                      className="flex items-center gap-2 p-2 hover:bg-background rounded cursor-pointer"
                      onClick={() => toggleColumn(colId)}
                    >
                      <Checkbox 
                        id={`col-${colId}`}
                        checked={selectedColumns.includes(colId)}
                        onCheckedChange={() => toggleColumn(colId)}
                        data-testid={`checkbox-col-${colId}`}
                      />
                      <label 
                        htmlFor={`col-${colId}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {availableColumns[colId]}
                      </label>
                    </div>
                  ))}
                </div>
                
                {selectedColumns.length === 0 && (
                  <p className="text-xs text-destructive mt-2">Selecione pelo menos uma coluna</p>
                )}
              </div>

              {/* Format Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Formato de exportação</Label>
                <div className="flex gap-3">
                  <Button
                    variant={exportFormat === 'excel' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => setExportFormat('excel')}
                    data-testid="btn-format-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    variant={exportFormat === 'csv' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => setExportFormat('csv')}
                    data-testid="btn-format-csv"
                  >
                    <FileText className="h-4 w-4" />
                    CSV
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setExportModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                variant="secondary" 
                onClick={handlePrint}
                disabled={selectedColumns.length === 0}
                className="gap-2"
                data-testid="btn-imprimir"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button 
                onClick={handleExport}
                disabled={selectedColumns.length === 0}
                className="gap-2"
                data-testid="btn-exportar"
              >
                <Download className="h-4 w-4" />
                Exportar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reversão de Alta Modal */}
        <Dialog open={showReversaoModal} onOpenChange={setShowReversaoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <RotateCcw className="h-5 w-5" />
                Reverter Alta
              </DialogTitle>
              <DialogDescription>
                {reversaoTarget && `Paciente: ${reversaoTarget.paciente_nome}. Esta ação irá cancelar a alta e reativar a internação.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="font-semibold">Justificativa da reversão *</Label>
                <Textarea
                  value={reversaoJustificativa}
                  onChange={(e) => setReversaoJustificativa(e.target.value)}
                  placeholder="Descreva o motivo da reversão da alta..."
                  rows={4}
                  className="resize-none"
                  data-testid="textarea-reversao-justificativa-lista"
                />
                {reversaoJustificativa.length > 0 && reversaoJustificativa.trim().length < 10 && (
                  <p className="text-xs text-red-500">Mínimo 10 caracteres</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReversaoModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmReversao}
                disabled={savingReversao || reversaoJustificativa.trim().length < 10}
                className="bg-green-600 hover:bg-green-700"
                data-testid="btn-confirmar-reversao-lista"
              >
                {savingReversao ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirmar reversão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Lista;
